/**
 * マッチメイキング用 React hook。
 *
 * 責務:
 *   - `createTicket()` → `pollUntilMatched()` の一連フローを状態管理
 *   - AbortController を保持してキャンセル可能にする
 *   - `elapsedMs` を 1 秒 tick で更新（UI のカウントダウン用）
 *   - `timeoutReached` を露出（クライアント側タイムアウト表示用）
 *   - アンマウント時に自動 cancel（リーク防止）
 *
 * 設計方針（.claude/rules/no-silent-fallback.md）:
 *   - エラーは state.error に格納し、かつ status='ERROR' に遷移させる。
 *   - start() 呼び出し側へ throw はしない（モーダル UI がクラッシュしないよう、
 *     エラーは状態として返す）。代わりに呼び出し側は start() の戻り値と status を
 *     組み合わせて判定する。
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  MATCHMAKING_TIMEOUT_MS,
  type MatchmakingStatus,
  type MatchmakingTicket,
  type MultiplayerMatchmakingState,
  type RoomInfo,
} from '../config/multiplayer'
import {
  cancelTicket,
  createTicket,
  MatchmakingCancelledError,
  MatchmakingTimeoutError,
  pollUntilMatched,
  type CreateTicketParams,
} from '../services/matchmaking'

const ELAPSED_TICK_MS = 1_000

interface UseMatchmakingResult extends MultiplayerMatchmakingState {
  /**
   * マッチメイキング開始。成功時は RoomInfo、失敗時は null を返す。
   * エラーの種別は state.status（TIMED_OUT / CANCELLED / ERROR）で判断できる。
   */
  start: (params?: CreateTicketParams) => Promise<RoomInfo | null>
  /** マッチメイキングをキャンセルする（既に終了していれば何もしない）。 */
  cancel: () => Promise<void>
  /** エラー・状態を IDLE にリセットする。 */
  reset: () => void
}

export function useMatchmaking(): UseMatchmakingResult {
  const [status, setStatus] = useState<MatchmakingStatus>('IDLE')
  const [ticket, setTicket] = useState<MatchmakingTicket | null>(null)
  const [room, setRoom] = useState<RoomInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [elapsedMs, setElapsedMs] = useState(0)

  const abortRef = useRef<AbortController | null>(null)
  const startedAtRef = useRef<number | null>(null)
  const activeTicketIdRef = useRef<string | null>(null)

  // 経過時間 tick（SEARCHING 中のみ走らせる）
  useEffect(() => {
    if (status !== 'SEARCHING') {
      return
    }
    const started = startedAtRef.current ?? Date.now()
    const tick = () => {
      setElapsedMs(Date.now() - started)
    }
    tick()
    const h = setInterval(tick, ELAPSED_TICK_MS)
    return () => clearInterval(h)
  }, [status])

  // アンマウント時に自動キャンセル（サーバー側チケットもクリーンアップ）
  useEffect(() => {
    return () => {
      const controller = abortRef.current
      if (controller && !controller.signal.aborted) {
        controller.abort()
      }
      const ticketId = activeTicketIdRef.current
      if (ticketId) {
        void cancelTicket(ticketId).catch((err) => {
          console.warn('[useMatchmaking] unmount-cancel failed:', err)
        })
      }
    }
  }, [])

  const resetInternal = useCallback(() => {
    abortRef.current = null
    startedAtRef.current = null
    activeTicketIdRef.current = null
    setStatus('IDLE')
    setTicket(null)
    setRoom(null)
    setError(null)
    setElapsedMs(0)
  }, [])

  const start = useCallback<UseMatchmakingResult['start']>(
    async (params) => {
      // 多重起動ガード: 既に走っている場合は一度キャンセルしてから再開する
      if (abortRef.current && !abortRef.current.signal.aborted) {
        abortRef.current.abort()
      }
      const previousTicketId = activeTicketIdRef.current
      if (previousTicketId) {
        try {
          await cancelTicket(previousTicketId)
        } catch (err) {
          console.warn('[useMatchmaking] previous ticket cancel failed:', err)
        }
      }

      const controller = new AbortController()
      abortRef.current = controller
      startedAtRef.current = Date.now()
      setError(null)
      setRoom(null)
      setElapsedMs(0)
      setStatus('SEARCHING')

      try {
        const created = await createTicket(params)
        activeTicketIdRef.current = created.ticketId
        setTicket(created)

        // 即時マッチ（Hub が空き部屋を即返したケース）
        if (created.status === 'MATCHED' && created.room) {
          activeTicketIdRef.current = null
          setRoom(created.room)
          setStatus('MATCHED')
          return created.room
        }

        const matchedRoom = await pollUntilMatched(created.ticketId, {
          signal: controller.signal,
          onStatusUpdate: (s) => {
            // サーバー側で TIMED_OUT / CANCELLED に遷移したら pollUntilMatched が throw するので、
            // ここでは SEARCHING → SEARCHING の維持だけ。UI ではスピナーだけ回す。
            if (s === 'SEARCHING') setStatus('SEARCHING')
          },
        })
        activeTicketIdRef.current = null
        setRoom(matchedRoom)
        setStatus('MATCHED')
        return matchedRoom
      } catch (err) {
        activeTicketIdRef.current = null
        if (err instanceof MatchmakingTimeoutError) {
          setStatus('TIMED_OUT')
          return null
        }
        if (err instanceof MatchmakingCancelledError) {
          setStatus('CANCELLED')
          return null
        }
        const message = err instanceof Error ? err.message : String(err)
        setError(message)
        setStatus('ERROR')
        console.warn('[useMatchmaking] start failed:', err)
        return null
      }
    },
    [],
  )

  const cancel = useCallback<UseMatchmakingResult['cancel']>(async () => {
    const controller = abortRef.current
    if (controller && !controller.signal.aborted) {
      controller.abort()
    }
    const ticketId = activeTicketIdRef.current
    activeTicketIdRef.current = null
    if (!ticketId) {
      // まだチケット発行前のキャンセル
      setStatus('CANCELLED')
      return
    }
    try {
      await cancelTicket(ticketId)
    } catch (err) {
      console.warn('[useMatchmaking] cancel failed:', err)
      // キャンセルが失敗してもユーザー操作としては「止めた」扱いにする
    }
    setStatus('CANCELLED')
  }, [])

  const reset = useCallback(() => {
    const controller = abortRef.current
    if (controller && !controller.signal.aborted) {
      controller.abort()
    }
    const ticketId = activeTicketIdRef.current
    if (ticketId) {
      void cancelTicket(ticketId).catch((err) => {
        console.warn('[useMatchmaking] reset-cancel failed:', err)
      })
    }
    resetInternal()
  }, [resetInternal])

  const timeoutReached =
    status === 'TIMED_OUT' ||
    (status === 'SEARCHING' &&
      startedAtRef.current !== null &&
      Date.now() - startedAtRef.current >= MATCHMAKING_TIMEOUT_MS)

  return {
    status,
    ticket,
    room,
    error,
    elapsedMs,
    timeoutReached,
    start,
    cancel,
    reset,
  }
}
